import { getIdCardError, isValidIdCard } from '../../shared/utils/idCardValidation';
import { escapeHtml } from '../../shared/utils/htmlEscape';
import { getSession } from '../../shared/auth/portalSession';
import { applySavedAppointment, releaseAppointment, type ScheduleRow as TimeSlot } from '../utils/scheduleState';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { getAdminScheduleApi, updateAppointmentSyncStatusApi, createAppointmentApi, updateAppointmentApi, getAvailableSlotsApi, type AppointmentData, type AdminScheduleSlot, type TimeSlot as ApiTimeSlot } from '../api/appointmentApi';
import { getServicesApi, type Service } from '../api/servicesApi';
import { getTechniciansApi, type TechnicianApiResponse } from '../api/techniciansApi';
import { APP_CONFIG } from '../constants/config';
import { NORTH_ISRAEL_VILLAGES, type Village } from '../constants/villages';

interface TimeSlotScheduleProps {
  selectedDate: string;
  onAppointmentCreated?: () => void;
  onScheduleLoaded?: (rows: AdminScheduleSlot[], day: string, scheduleType: 'US' | 'Xray') => void;
  userName?: string;
  searchTerm?: string;
  syncFilter?: 'All' | 'Synced' | 'NotSynced';
  serviceFilter?: string;
  providerFilter?: string;
}

const SkeletonBlock: React.FC<{ className: string }> = ({ className }) => (
  <div className={`animate-pulse rounded-md bg-slate-200 ${className}`} />
);

const ScheduleSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <div className="p-4 border-b border-slate-200 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <SkeletonBlock className="h-7 w-80 max-w-full" />
        <SkeletonBlock className="h-10 w-28" />
      </div>
      <div className="flex gap-2">
        <SkeletonBlock className="h-10 w-36" />
        <SkeletonBlock className="h-10 w-36" />
      </div>
      <SkeletonBlock className="h-4 w-56" />
    </div>

    <div className="overflow-x-auto p-4">
      <div className="space-y-3 min-w-[900px]">
        <div className="grid grid-cols-10 gap-3">
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonBlock key={`schedule-header-${index}`} className="h-4 w-full" />
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, rowIndex) => (
          <div key={`schedule-row-${rowIndex}`} className="grid grid-cols-10 gap-3 items-center">
            {Array.from({ length: 10 }).map((__, cellIndex) => (
              <SkeletonBlock key={`schedule-row-${rowIndex}-cell-${cellIndex}`} className="h-9 w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TimeSlotSchedule: React.FC<TimeSlotScheduleProps> = ({
  selectedDate,
  onAppointmentCreated,
  onScheduleLoaded,
  userName,
  searchTerm = '', syncFilter = 'All', serviceFilter = 'All', providerFilter = 'All'
}) => {
  const [activeTab, setActiveTab] = useState<'US' | 'Xray'>('US');
  const [slots, setSlotsState] = useState<TimeSlot[]>([]);
  const viewKey = `${selectedDate}|${activeTab}`;
  const currentView = useRef(viewKey);
  currentView.current = viewKey;
  const setSlots: React.Dispatch<React.SetStateAction<TimeSlot[]>> = value => {
    // A mutation started on a previous date must not append rows to this date.
    if (currentView.current === viewKey) setSlotsState(value);
  };
  const slotRequest = useRef(0);
  const [loadedView, setLoadedView] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState<string | null>(null);
  const setError: React.Dispatch<React.SetStateAction<string | null>> = value => {
    if (currentView.current === viewKey) setErrorState(value);
  };
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<TimeSlot | null>(null);
  const [bookingForm, setBookingForm] = useState({
    fullName: '',
    phoneNumber: '',
    idCard: '',
    village: '',
    provider: '',
    serviceId: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [idCardTouched, setIdCardTouched] = useState(false);
  const visibleIdCardError = idCardTouched ? getIdCardError(bookingForm.idCard) : null;

  useEffect(() => {
    if (!showBookingModal) setIdCardTouched(false);
  }, [showBookingModal]);
  const [filteredVillages, setFilteredVillages] = useState<Village[]>([]);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);
  const [reserveTime, setReserveTime] = useState('');
  const [isReserveSlot, setIsReserveSlot] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<TimeSlot | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [closingSlot, setClosingSlot] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [appointmentToMove, setAppointmentToMove] = useState<TimeSlot | null>(null);
  const [moveNewDate, setMoveNewDate] = useState('');
  const [moveAvailableSlots, setMoveAvailableSlots] = useState<ApiTimeSlot[]>([]);
  const [moveSelectedTime, setMoveSelectedTime] = useState('');
  const [movingAppointment, setMovingAppointment] = useState(false);
  const [loadingMoveSlots, setLoadingMoveSlots] = useState(false);

  const handleSendWhatsAppReminder = (slot: TimeSlot) => {
    if (!slot.phoneNumber || !slot.patientName) return;

    // Format phone number for WhatsApp (remove leading 0, add 972)
    let formattedPhone = slot.phoneNumber.replace(/\D/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = '972' + formattedPhone.substring(1);
    } else if (!formattedPhone.startsWith('972')) {
      formattedPhone = '972' + formattedPhone;
    }

    // Get appointment time from slot
    const appointmentTime = slot.time;
    const appointmentDate = selectedDate;

    // Patients must receive the public confirmation portal, never the admin origin.
    const confirmationPortalUrl = import.meta.env.VITE_CONFIRM_PORTAL_URL || 'https://c.muna.center';
    const confirmationUrl = slot.confirmationToken
      ? `${confirmationPortalUrl.replace(/\/$/, '')}/${slot.confirmationToken}`
      : '';

    // Create WhatsApp message (Arabic by default, similar to SMS template)
    let message = `مرحبا ${slot.patientName},
لديك دور في مركز منى الطبي - كفر كنا
يرجى تأكيد حضورك غداً الساعة ${appointmentTime} لفحص التراساوند`;

    // Add confirmation link if available
    if (confirmationUrl) {
      message += `\nاضغط/ي لتأكيد حضورك\n${confirmationUrl}`;
    }

    // Open WhatsApp with the message
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    const controller = new AbortController();
    loadSlots(controller.signal);
    return () => { controller.abort(); slotRequest.current += 1; };
  }, [selectedDate, activeTab]);

  useEffect(() => {
    let current = true;
    loadServices(() => current);
    getTechniciansApi().then(data => { if (current) setTechnicians(data); })
      .catch(error => console.error('Failed to load technicians:', error));
    return () => { current = false; };
  }, [selectedDate]);

  useEffect(() => {
    setShowBookingModal(false); setSelectedSlot(null);
    setShowCancelModal(false); setAppointmentToCancel(null);
    setShowMoveModal(false); setAppointmentToMove(null);
    setOpenDropdownId(null);
  }, [selectedDate]);

  useEffect(() => {
    if (bookingForm.village) {
      const filtered = NORTH_ISRAEL_VILLAGES.filter(v =>
        v.name.includes(bookingForm.village)
      );
      setFilteredVillages(filtered);
    } else {
      setFilteredVillages(NORTH_ISRAEL_VILLAGES);
    }
  }, [bookingForm.village]);

  const loadServices = async (isCurrent = () => currentView.current === viewKey) => {
    try {
      const data = await getServicesApi(selectedDate);
      if (isCurrent()) setServices(data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const loadSlots = async (signal?: AbortSignal) => {
    const request = ++slotRequest.current;
    const isCurrent = () => request === slotRequest.current && !signal?.aborted;
    try {
      setLoading(true);
      setError(null);

      // Use the new admin schedule API to get all slots with appointment details
      const adminSchedule = await getAdminScheduleApi(selectedDate, activeTab, signal);
      if (!isCurrent()) return;

      // Convert admin schedule to TimeSlot format
      const timeSlots: TimeSlot[] = adminSchedule.flatMap(slot => {
        // If slot has appointments, create a TimeSlot for each appointment
        if (slot.appointments && slot.appointments.length > 0) {
          return slot.appointments.map(apt => ({
            time: slot.time,
            isAvailable: false,
            intervalMinutes: slot.intervalMinutes,
            date: selectedDate,
            capacity: slot.capacity,
            booked: slot.booked,
            technicianId: slot.technicianId,
            technicianName: slot.technicianName,
            appointmentId: apt.appointmentId,
            patientName: apt.patientName,
            phoneNumber: apt.phoneNumber,
            idCard: apt.idCard,
            provider: apt.provider,
            isSynced: apt.isSynced,
            clinicName: apt.clinicName,
            createdBy: apt.createdBy,
            serviceName: apt.serviceName,
            serviceId: apt.serviceId,
            village: apt.village,
            isReserveSlot: apt.isReserveSlot,
            status: apt.status,
            confirmationStatus: apt.confirmationStatus,
            confirmationToken: apt.confirmationToken,
            reminderSent: apt.reminderSent,
            appointments: slot.appointments,
          }));
        }

        // Empty slot - available for booking
        return [{
          time: slot.time,
          isAvailable: slot.isAvailable,
          intervalMinutes: slot.intervalMinutes,
          date: selectedDate,
          capacity: slot.capacity,
          booked: slot.booked,
          technicianId: slot.technicianId,
          technicianName: slot.technicianName,
          appointments: [],
        }];
      });

      setSlots(timeSlots);
      setLoadedView(viewKey);
      onScheduleLoaded?.(adminSchedule, selectedDate, activeTab);
    } catch (err) {
      if (!isCurrent()) return;
      setError('Failed to load time slots');
      console.error(err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleToggleSync = async (appointmentId: string) => {
    if (!appointmentId) {
      console.error('No appointmentId provided');
      return;
    }

    try {
      setSyncingIds(prev => new Set(prev).add(appointmentId));

      // Get current sync status
      const currentSlot = slots.find(s => s.appointmentId === appointmentId);
      const newSyncStatus = !currentSlot?.isSynced;

      console.log('Updating sync status:', { appointmentId, currentStatus: currentSlot?.isSynced, newStatus: newSyncStatus });

      // Call API to update sync status
      const result = await updateAppointmentSyncStatusApi(appointmentId, newSyncStatus);
      console.log('Sync status update successful:', result);

      // Update local state
      setSlots(prevSlots =>
        prevSlots.map(slot =>
          slot.appointmentId === appointmentId
            ? { ...slot, isSynced: newSyncStatus }
            : slot
        )
      );

    } catch (err) {
      console.error('Failed to toggle sync status:', err);
      setError(`עדכון סטטוס סנכרון נכשל: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSyncingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
    }
  };

  const handleBookSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setIsReserveSlot(false);
    setIsEditMode(false);
    setEditingAppointment(null);
    setBookingForm({
      fullName: '',
      phoneNumber: '',
      idCard: '',
      village: '',
      provider: '',
      serviceId: '',
    });
    // Ensure services are loaded before showing modal
    if (services.length === 0) {
      loadServices().then(() => {
        setShowBookingModal(true);
      });
    } else {
      setShowBookingModal(true);
    }
  };

  const handleEditAppointment = (slot: TimeSlot) => {
    console.log('Editing appointment:', {
      slot,
      serviceName: slot.serviceName,
      serviceId: slot.serviceId,
      availableServices: services.map(s => ({ id: s.service_id, name: s.name }))
    });

    setSelectedSlot(slot);
    setIsReserveSlot(false);
    setIsEditMode(true);
    setEditingAppointment(slot);

    // Find the service ID from service name if serviceId is not available
    let serviceIdToUse = slot.serviceId || '';
    if (!serviceIdToUse && slot.serviceName && services.length > 0) {
      const matchingService = services.find(s => s.name === slot.serviceName);
      if (matchingService) {
        serviceIdToUse = matchingService.service_id.toString();
        console.log('Found matching service by name:', matchingService);
      }
    }

    // Pre-populate form with existing data
    setBookingForm({
      fullName: slot.patientName || '',
      phoneNumber: slot.phoneNumber || '',
      idCard: slot.idCard || '',
      village: slot.village || '',
      provider: slot.provider || '',
      serviceId: serviceIdToUse,
    });

    console.log('Form populated with:', {
      village: slot.village,
      serviceId: serviceIdToUse,
      serviceName: slot.serviceName
    });

    // Ensure services are loaded before showing modal
    if (services.length === 0) {
      loadServices().then(() => {
        setShowBookingModal(true);
      });
    } else {
      setShowBookingModal(true);
    }
  };

  const handleCreateReserveSlot = () => {
    const defaultInterval = slots.length > 0 ? (slots[0].intervalMinutes || 5) : 5;

    // Calculate the next available time after the last slot
    let suggestedTime = '';
    if (slots.length > 0) {
      const lastSlot = slots[slots.length - 1];
      const [hours, minutes] = lastSlot.time.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + (lastSlot.intervalMinutes || defaultInterval);
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      suggestedTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    }

    // Create a temporary slot for reserve
    const reserveSlot: TimeSlot = {
      time: suggestedTime, // Pre-filled with suggested time
      isAvailable: true,
      intervalMinutes: defaultInterval,
    };

    setSelectedSlot(reserveSlot);
    setIsReserveSlot(true);
    setReserveTime(suggestedTime);
    setBookingForm({
      fullName: '',
      phoneNumber: '',
      village: '',
      provider: '',
      serviceId: '',
    });

    // Ensure services are loaded before showing modal
    if (services.length === 0) {
      loadServices().then(() => {
        setShowBookingModal(true);
      });
    } else {
      setShowBookingModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowBookingModal(false);
    setSelectedSlot(null);
    setIsEditMode(false);
    setEditingAppointment(null);
    setBookingForm({
      fullName: '',
      phoneNumber: '',
      idCard: '',
      village: '',
      provider: '',
      serviceId: '',
    });
  };

  // Filter services based on technician's supported services and active status
  const filteredServices = useMemo(() => {
    // First filter by active status
    const activeServices = services.filter(service => service.isActive);

    // If no technician or Xray tab, return all active services
    if (!selectedSlot?.technicianId || activeTab === 'Xray') {
      console.log('Using services filtered by backend:', {
        selectedDate,
        activeTab,
        totalServices: services.length,
        filteredCount: activeServices.length,
        filteredServices: activeServices
      });
      return activeServices;
    }

    // Filter by technician's supported services
    const technician = technicians.find(t => t.id === selectedSlot.technicianId);
    if (!technician || !technician.supportedServiceIds || technician.supportedServiceIds.length === 0) {
      console.log('No technician found or no supported services, returning all active services');
      return activeServices;
    }

    const technicianServices = activeServices.filter(service =>
      technician.supportedServiceIds.includes(service.service_id.toString())
    );

    console.log('Services filtered by technician:', {
      technicianId: selectedSlot.technicianId,
      technicianName: selectedSlot.technicianName,
      supportedServiceIds: technician.supportedServiceIds,
      activeServicesCount: activeServices.length,
      activeServicesIds: activeServices.map(s => ({ id: s.service_id, name: s.name })),
      filteredCount: technicianServices.length,
      filteredServices: technicianServices.map(s => ({ id: s.service_id, name: s.name }))
    });

    return technicianServices;
  }, [services, technicians, selectedSlot?.technicianId, activeTab, selectedDate]);

  const handleOpenCancelModal = (slot: TimeSlot) => {
    setAppointmentToCancel(slot);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setAppointmentToCancel(null);
    setCancelReason('');
  };

  const handleCloseSlot = async (slot: TimeSlot) => {
    setClosingSlot(true);
    setOpenDropdownId(null);
    try {
      const appointmentData: AppointmentData = {
        fullName: '-',
        phoneNumber: '-',
        idCard: '',
        village: '-',
        provider: '-',
        serviceType: activeTab === 'US' ? 2 : 1,
        serviceId: '',
        serviceName: '-',
        selectedDay: selectedDate,
        selectedDayLabel: new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' }),
        selectedTime: slot.time,
        intervalMinutes: slot.intervalMinutes,
        clinicName: APP_CONFIG.CLINIC_NAME,
        createdBy: userName || APP_CONFIG.DEFAULT_CREATED_BY,
        status: 'closed',
      };

      const result = await createAppointmentApi(appointmentData);

      toast.success('השעה נחסמה בהצלחה', {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#3f3f3fff',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '8px',
        },
      });

      // Update local state
      setSlots(prevSlots =>
        prevSlots.map(s =>
          s.time === slot.time
            ? {
                ...s,
                isAvailable: false,
                appointmentId: result.appointmentId,
                patientName: '-',
                phoneNumber: '-',
                provider: '-',
                serviceName: '-',
                clinicName: APP_CONFIG.CLINIC_NAME,
                createdBy: userName || APP_CONFIG.DEFAULT_CREATED_BY,
                status: 'closed',
              }
            : s
        )
      );

      // No need to call onAppointmentCreated() - local state is already updated
    } catch (err) {
      console.error('Failed to close slot:', err);
      toast.error('שגיאה בחסימת השעה', {
        duration: 3000,
        position: 'top-right',
      });
    } finally {
      setClosingSlot(false);
    }
  };

  const handleOpenSlot = async (slot: TimeSlot) => {
    if (!slot.appointmentId) return;

    setClosingSlot(true);
    setOpenDropdownId(null);
    try {
      // Delete the appointment from DB by updating with cancelled status and a special flag
      // Since we don't have a delete endpoint, we'll cancel it with a special reason
      const appointmentData: AppointmentData = {
        appointmentId: slot.appointmentId,
        fullName: slot.patientName || '',
        phoneNumber: slot.phoneNumber || '',
        idCard: '',
        village: '',
        provider: slot.provider || '',
        serviceType: activeTab === 'US' ? 2 : 1,
        serviceId: '',
        serviceName: slot.serviceName,
        selectedDay: selectedDate,
        selectedDayLabel: new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' }),
        selectedTime: slot.time,
        clinicName: slot.clinicName,
        createdBy: slot.createdBy,
        status: 'cancelled',
        cancelReason: 'slot_opened_by_admin',
      };

      await createAppointmentApi(appointmentData);

      toast.success('השעה נפתחה בהצלחה', {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '8px',
        },
      });

      // Update local state - make slot available
      setSlots(prevSlots =>
        prevSlots.map(s =>
          s.appointmentId === slot.appointmentId
            ? {
                ...s,
                isAvailable: true,
                appointmentId: undefined,
                patientName: undefined,
                phoneNumber: undefined,
                idCard: undefined,
                provider: undefined,
                serviceName: undefined,
                serviceId: undefined,
                village: undefined,
                clinicName: undefined,
                createdBy: undefined,
                isSynced: false,
                status: undefined,
                confirmationStatus: undefined,
                reminderSent: undefined
              }
            : s
        )
      );

      // No need to call onAppointmentCreated() - local state is already updated
    } catch (err) {
      console.error('Failed to open slot:', err);
      toast.error('שגיאה בפתיחת השעה', {
        duration: 3000,
        position: 'top-right',
      });
    } finally {
      setClosingSlot(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel || !cancelReason) {
      toast.error('נא לבחור סיבת ביטול', {
        duration: 3000,
        position: 'top-right',
      });
      return;
    }

    setCancelling(true);
    try {
      const appointmentData: AppointmentData = {
        appointmentId: appointmentToCancel.appointmentId,
        fullName: appointmentToCancel.patientName || '',
        phoneNumber: appointmentToCancel.phoneNumber || '',
        idCard: '',
        village: '',
        provider: appointmentToCancel.provider || '',
        serviceType: activeTab === 'US' ? 2 : 1,
        serviceId: '',
        serviceName: appointmentToCancel.serviceName,
        selectedDay: selectedDate,
        selectedDayLabel: new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' }),
        selectedTime: appointmentToCancel.time,
        clinicName: appointmentToCancel.clinicName,
        createdBy: appointmentToCancel.createdBy,
        status: 'cancelled',
        cancelReason: cancelReason,
      };

      await createAppointmentApi(appointmentData);

      // Show success toast
      toast.success('התור בוטל בהצלחה', {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '8px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#10b981',
        },
      });

      // Remove the cancelled appointment from slots (make it available again)
      setSlots(prevSlots =>
        prevSlots.map(slot =>
          slot.appointmentId === appointmentToCancel.appointmentId
            ? {
                ...slot,
                isAvailable: true,
                appointmentId: undefined,
                patientName: undefined,
                phoneNumber: undefined,
                idCard: undefined,
                provider: undefined,
                serviceName: undefined,
                serviceId: undefined,
                village: undefined,
                clinicName: undefined,
                createdBy: undefined,
                isSynced: false,
                status: undefined,
                confirmationStatus: undefined,
                reminderSent: undefined
              }
            : slot
        )
      );

      handleCloseCancelModal();

      // No need to call onAppointmentCreated() - local state is already updated
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
      toast.error('שגיאה בביטול התור', {
        duration: 3000,
        position: 'top-right',
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleOpenMoveModal = (slot: TimeSlot) => {
    console.log('Opening move modal for appointment:', {
      appointmentId: slot.appointmentId,
      serviceName: slot.serviceName,
      serviceId: slot.serviceId,
      patientName: slot.patientName,
      time: slot.time
    });

    setAppointmentToMove(slot);
    setMoveNewDate('');
    setMoveAvailableSlots([]);
    setMoveSelectedTime('');
    setShowMoveModal(true);
  };

  const handleCloseMoveModal = () => {
    setShowMoveModal(false);
    setAppointmentToMove(null);
    setMoveNewDate('');
    setMoveAvailableSlots([]);
    setMoveSelectedTime('');
  };

  const handleMoveAppointment = async () => {
    if (!appointmentToMove || !moveNewDate || !moveSelectedTime) {
      toast.error('נא לבחור תאריך ושעה חדשים', {
        duration: 3000,
        position: 'top-right',
      });
      return;
    }

    setMovingAppointment(true);
    try {
      // Update the appointment with new date and time
      const appointmentData: AppointmentData = {
        appointmentId: appointmentToMove.appointmentId,
        fullName: appointmentToMove.patientName || '',
        phoneNumber: appointmentToMove.phoneNumber || '',
        idCard: appointmentToMove.idCard || '',
        village: appointmentToMove.village || '',
        provider: appointmentToMove.provider || '',
        serviceType: activeTab === 'US' ? 2 : 1,
        serviceId: appointmentToMove.serviceId || '',
        serviceName: appointmentToMove.serviceName || '',
        selectedDay: moveNewDate,
        selectedDayLabel: new Date(moveNewDate).toLocaleDateString('he-IL', { weekday: 'long' }),
        selectedTime: moveSelectedTime,
        intervalMinutes: appointmentToMove.intervalMinutes,
        clinicName: appointmentToMove.clinicName || '',
        createdBy: appointmentToMove.createdBy || '',
        status: appointmentToMove.status || 'completed',
        isSynced: false, // Reset sync status when moved
      };

      const result = await createAppointmentApi(appointmentData);
      if (currentView.current !== viewKey) return;
      const saved = { ...appointmentData, ...result };

      toast.success('התור הועבר בהצלחה', {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '8px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#10b981',
        },
      });

      setSlots(prevSlots => saved.selectedDay === selectedDate
        ? applySavedAppointment(prevSlots, saved, appointmentToMove)
        : releaseAppointment(prevSlots, appointmentToMove.appointmentId!));

      handleCloseMoveModal();

      // No need to call onAppointmentCreated() - appointment is removed from current date view
    } catch (err) {
      console.error('Failed to move appointment:', err);
      toast.error(`שגיאה בהעברת התור: ${err instanceof Error ? err.message : 'Unknown error'}`, {
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setMovingAppointment(false);
    }
  };

  const handleLoadMoveSlotsForDate = async (date: string) => {
    if (!appointmentToMove || !date) return;

    setLoadingMoveSlots(true);
    setMoveAvailableSlots([]);
    setMoveSelectedTime('');

    try {
      // Get the service type from the appointment
      const serviceType = activeTab === 'US' ? 2 : 1;

      // Get serviceId - if not available, look it up from serviceName
      let serviceId = appointmentToMove.serviceId;
      if (!serviceId && appointmentToMove.serviceName) {
        const matchingService = services.find(s => s.name === appointmentToMove.serviceName);
        if (matchingService) {
          serviceId = matchingService.service_id.toString();
          console.log('Looked up serviceId from serviceName:', { serviceName: appointmentToMove.serviceName, serviceId });
        }
      }

      if (!serviceId) {
        console.error('No serviceId found for appointment:', appointmentToMove);
        toast.error('לא ניתן לטעון שעות - חסר מזהה שירות', {
          duration: 3000,
          position: 'top-right',
        });
        setLoadingMoveSlots(false);
        return;
      }

      console.log('Loading slots for move:', {
        date,
        serviceType,
        serviceId,
        serviceName: appointmentToMove.serviceName
      });

      // Load available slots for the selected date with the serviceId
      // Backend will filter technicians who support this service
      const slotsData = await getAvailableSlotsApi(date, serviceType, serviceId);

      console.log('Available slots loaded:', slotsData.length);

      if (slotsData.length === 0) {
        toast.error(`לא נמצאו שעות פנויות בתאריך זה עבור ${appointmentToMove.serviceName}`, {
          duration: 3000,
          position: 'top-right',
        });
      }

      setMoveAvailableSlots(slotsData);
    } catch (err) {
      console.error('Failed to load slots for move:', err);
      toast.error('שגיאה בטעינת שעות פנויות', {
        duration: 3000,
        position: 'top-right',
      });
      setMoveAvailableSlots([]);
    } finally {
      setLoadingMoveSlots(false);
    }
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setIdCardTouched(true);
    if (!selectedSlot) return;
    const idCardError = getIdCardError(bookingForm.idCard);
    if (idCardError) {
      toast.error(idCardError);
      return;
    }

    // Validate reserve slot time
    if (isReserveSlot && !reserveTime) {
      alert('נא לבחור שעת תור');
      return;
    }

    // Check if reserve slot time already exists
    if (isReserveSlot && reserveTime) {
      const existingSlot = slots.find(s => s.time === reserveTime);
      if (existingSlot && !existingSlot.isAvailable) {
        alert('שעה זו כבר תפוסה');
        return;
      }
    }

    setSubmitting(true);
    try {
      const selectedService = services.find(s => s.service_id === bookingForm.serviceId);
      const appointmentTime = isReserveSlot ? reserveTime : selectedSlot.time;

      const appointmentData: AppointmentData = {
        fullName: bookingForm.fullName,
        phoneNumber: bookingForm.phoneNumber,
        idCard: bookingForm.idCard || '',
        village: bookingForm.village,
        provider: bookingForm.provider,
        serviceType: activeTab === 'US' ? 2 : 1,
        serviceId: bookingForm.serviceId,
        serviceName: selectedService?.name,
        selectedDay: selectedDate,
        selectedDayLabel: new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' }),
        selectedTime: appointmentTime,
        intervalMinutes: selectedSlot.intervalMinutes || 5,
        clinicName: editingAppointment?.clinicName || APP_CONFIG.CLINIC_NAME,
        createdBy: editingAppointment?.createdBy || userName || APP_CONFIG.DEFAULT_CREATED_BY,
        status: 'completed',
        isReserveSlot: isEditMode ? Boolean(editingAppointment?.isReserveSlot) : isReserveSlot,
      };

      let result;
      let isUpdating = false;

      // If in edit mode, preserve existing confirmation data
      if (isEditMode && editingAppointment?.appointmentId) {
        isUpdating = true;
        // Get the existing appointment data from slots to preserve confirmation fields
        const existingAppt = slots.find(s => s.appointmentId === editingAppointment.appointmentId);

        // Create updated appointment preserving confirmation fields and including appointmentId
        const updatedData = {
          ...appointmentData,
          appointmentId: editingAppointment.appointmentId, // Include appointmentId for backend to update
          // Preserve existing confirmation/SMS fields from the existing appointment
          confirmationToken: existingAppt?.confirmationStatus ? 'preserved' : undefined,
          confirmationStatus: existingAppt?.confirmationStatus,
          reminderSent: existingAppt?.reminderSent,
          tokenGeneratedAt: existingAppt ? 'preserved' : undefined,
        };

        console.log('Updating appointment with data:', updatedData);
        result = await createAppointmentApi(updatedData);

        toast.success('התור עודכן בהצלחה!', {
          duration: 3000,
          position: 'top-right',
          style: {
            background: '#3b82f6',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '8px',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#3b82f6',
          },
        });
      } else {
        // Create new appointment
        console.log('Creating new appointment with data:', appointmentData);
        result = await createAppointmentApi(appointmentData);

        // Show success toast for new appointments
        if (isReserveSlot) {
          toast.success('תור שמור נוצר בהצלחה!', {
            duration: 5000,
            position: 'top-right',
            style: {
              background: '#10b981',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '8px',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          });
        } else {
          toast.success('התור נקבע בהצלחה!', {
            duration: 3000,
            position: 'top-right',
            style: {
              background: '#10b981',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '8px',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          });
        }
      }

      if (currentView.current !== viewKey) return;
      setSlots(prevSlots => applySavedAppointment(prevSlots, { ...appointmentData, ...result }, selectedSlot));

      handleCloseModal();

      // No need to call onAppointmentCreated() - local state is already updated
    } catch (err) {
      console.error('Failed to save appointment:', err);
      const errorMessage = isEditMode ? 'שגיאה בעדכון התור' : 'שגיאה ביצירת התור';
      const errorDetail = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error details:', errorDetail);

      toast.error(`${errorMessage}: ${errorDetail}`, {
        duration: 5000,
        position: 'top-right',
        style: {
          background: '#ef4444',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
          padding: '16px',
          borderRadius: '8px',
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const visibleSlots = useMemo(() => slots.filter(slot => slot.isAvailable || (
    (!searchTerm || (slot.idCard || '').includes(searchTerm))
    && (syncFilter === 'All' || (syncFilter === 'Synced' ? slot.isSynced : !slot.isSynced))
    && (serviceFilter === 'All' || slot.serviceName === serviceFilter)
    && (providerFilter === 'All' || slot.provider === providerFilter)
  )), [slots, searchTerm, syncFilter, serviceFilter, providerFilter]);

  const exportToPDF = () => {
    try {
      console.log('Starting PDF export...');

      // Prepare table data - only booked appointments
      const bookedSlots = visibleSlots.filter(slot => !slot.isAvailable);
      console.log('Booked slots found:', bookedSlots.length);

      // Create HTML content for printing
      const title = `לוח זמנים - ${activeTab === 'US' ? 'אולטרסאונד' : 'צילום רנטגן'}`;
      const date = new Date(selectedDate).toLocaleDateString('he-IL', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const currentUser = getSession<{ name?: string; email?: string }>('admin')?.user;
      const currentUserName = userName || currentUser?.name || currentUser?.email || 'משתמש לא מזוהה';

      const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="he">
        <head>
          <meta charset="UTF-8">
          <title>${escapeHtml(title)}</title>
          <style>
            @media print {
              @page {
                margin: 1cm;
                size: A4;
              }
              body {
                margin: 0;
                font-family: Arial, sans-serif;
              }
            }
            body {
              font-family: Arial, sans-serif;
              direction: rtl;
              text-align: right;
              padding: 20px;
            }
            .clinic-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 3px solid #0f766e;
              padding-bottom: 15px;
            }
            .clinic-header h1 {
              color: #0f766e;
              margin: 0;
              font-size: 28px;
              font-weight: bold;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #334155;
              padding-bottom: 15px;
            }
            .header h2 {
              color: #334155;
              margin: 0 0 10px 0;
              font-size: 22px;
            }
            .header h3 {
              color: #64748b;
              margin: 0;
              font-size: 16px;
              font-weight: normal;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 14px;
            }
            th, td {
              border: 1px solid #cbd5e1;
              padding: 12px 8px;
              text-align: right;
            }
            th {
              background-color: #475569;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f8fafc;
            }
            tr:nth-child(odd) {
              background-color: #f1f5f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #64748b;
              font-size: 12px;
            }
            .no-appointments {
              text-align: center;
              color: #64748b;
              font-style: italic;
              padding: 20px;
            }
          </style>
        </head>
        <body>
          <div class="clinic-header">
            <h1>מרכז מונא הרפואי</h1>
          </div>

          <div class="header">
            <h2>${escapeHtml(title)}</h2>
            <h3>${escapeHtml(date)}</h3>
          </div>

          ${bookedSlots.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th>שעה</th>
                <th>ת.ז.</th>
                <th>שם מטופל</th>
                <th>טלפון</th>
                <th>קופת חולים</th>
                <th>שירות</th>
              </tr>
            </thead>
            <tbody>
              ${bookedSlots.map(slot => `
                <tr>
                  <td>${escapeHtml(slot.time || '-')}</td>
                  <td>${escapeHtml(slot.idCard || '-')}</td>
                  <td>${escapeHtml(slot.patientName || '-')}</td>
                  <td>${escapeHtml(slot.phoneNumber || '-')}</td>
                  <td>${escapeHtml(slot.provider || '-')}</td>
                  <td>${escapeHtml(slot.serviceName || '-')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>סה״כ תורים: ${escapeHtml(bookedSlots.length)}</p>
            <p>הודפס ב: ${escapeHtml(new Date().toLocaleString('he-IL'))}</p>
            <p>הודפס על ידי: ${escapeHtml(currentUserName)}</p>
          </div>
          ` : `
          <div class="no-appointments">
            <p>אין תורים קבועים עבור תאריך זה</p>
          </div>
          `}
        </body>
        </html>
      `;

      // Open new window and print
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      } else {
        alert('נא לאפשר חלונות קופצים כדי להדפיס');
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('שגיאה בהורדת ה-PDF');
    }
  };

  if (loading || (!error && loadedView !== viewKey)) {
    return <ScheduleSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-red-600" role="alert">{error}</p>
        <button className="mt-3 underline" onClick={() => loadSlots()}>נסה שוב</button>
      </div>
    );
  }

  const groupedSlots = visibleSlots.reduce((acc, slot) => {
    const hour = slot.time.split(':')[0];
    if (!acc[hour]) acc[hour] = [];
    acc[hour].push(slot);
    return acc;
  }, {} as Record<string, TimeSlot[]>);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <Toaster />
      <div className="p-4 border-b border-slate-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-slate-800">
            לוח זמנים - {new Date(selectedDate).toLocaleDateString('he-IL', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h3>

          {/* Print Button */}
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-6 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold border border-slate-600 hover:border-slate-500"
            title="הדפס"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>הדפס</span>
          </button>
        </div>

        {/* Tabs for US and Xray */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('US')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'US'
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            אולטרסאונד (US)
          </button>
          {APP_CONFIG.ENABLE_XRAY && (
            <button
              onClick={() => setActiveTab('Xray')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'Xray'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              צילום רנטגן (Xray)
            </button>
          )}
        </div>

        <p className="text-sm text-slate-500 mt-3">
          תפוס: {visibleSlots.filter(s => !s.isAvailable).length} |
          פנוי: {visibleSlots.filter(s => s.isAvailable).length} |
          סה"כ: {visibleSlots.length}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-fixed divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className="w-24 px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                שעה
              </th>
              <th scope="col" className="w-24 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                ת.ז.
              </th>
              <th scope="col" className="w-32 px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                שם מטופל
              </th>
              <th scope="col" className="w-28 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                טלפון
              </th>

              <th scope="col" className="w-32 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                שירות
              </th>
              <th scope="col" className="w-24 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                קופת חולים
              </th>
              <th scope="col" className="w-16 px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" title="סטטוס אישור">
                 אישור הגעה
              </th>
              <th scope="col" className="w-10 px-1 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" title="נרשם במערכת החיצונית">
                <span className="text-base">✓</span>
              </th>
              <th scope="col" className="w-32 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                מרפאה / נוצר ע"י
              </th>
              <th scope="col" className="w-32 px-2 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                פעולות
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {Object.keys(groupedSlots).sort().map(hour => (
              <React.Fragment key={hour}>
                {groupedSlots[hour].map((slot, idx) => (
                  <tr
                    key={slot.appointmentId || `${slot.time}-${slot.technicianId || "empty"}`}
                    className={`${
                      slot.isAvailable
                        ? 'bg-green-50 hover:bg-green-100 border-r-4 border-green-500'
                        : slot.patientName === '-'
                        ? 'bg-slate-200 hover:bg-slate-300 border-r-4 border-slate-500'
                        : slot.isSynced
                        ? 'bg-blue-50 hover:bg-blue-100 border-r-4 border-blue-500'
                        : 'bg-orange-50 hover:bg-orange-100 border-r-4 border-orange-500'
                    } transition-colors`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {slot.isAvailable ? (
                          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        ) : slot.patientName === '-' ? (
                          <div className="w-2 h-2 rounded-full bg-slate-500"></div>
                        ) : slot.isSynced ? (
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-slate-900">{slot.time}</span>
                          {slot.technicianName && (
                            <span className="text-[9px] text-slate-500 font-normal" style={{ opacity: 0.7 }}>
                              {slot.technicianName}
                            </span>
                          )}
                        </div>
                        {slot.isReserveSlot && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded border border-amber-300">
                            Reserve
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-slate-600">
                      {slot.idCard || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">
                      {slot.patientName || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-slate-600">
                      {slot.phoneNumber || '-'}
                    </td>

                    <td className="px-2 py-2 whitespace-nowrap text-xs text-slate-600">
                      {slot.serviceName || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-slate-600">
                      {slot.provider || '-'}
                    </td>
                    <td className="px-2 py-2">
                      {!slot.isAvailable && slot.appointmentId && slot.status !== 'closed' && slot.status !== 'cancelled' && slot.confirmationStatus ? (
                        <div className="flex justify-center items-center gap-1">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${
                            slot.confirmationStatus === 'confirmed'
                              ? 'bg-green-100 text-green-800'
                              : slot.confirmationStatus === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {slot.confirmationStatus === 'confirmed' ? '✓ אושר' :
                             slot.confirmationStatus === 'cancelled' ? '✗ בוטל' :
                             'ממתין'}
                          </span>
                          {slot.confirmationStatus === 'pending' && (
                            <button
                              onClick={() => handleSendWhatsAppReminder(slot)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="שלח תזכורת בוואטסאפ"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <span className="text-slate-400 text-xs">-</span>
                        </div>
                      )}
                    </td>
                    <td className="px-1 py-2">
                      {!slot.isAvailable && slot.appointmentId && slot.status !== 'closed' && slot.status !== 'cancelled' ? (
                        <div className="flex justify-center" title={slot.isSynced ? 'נרשם במערכת החיצונית' : 'לא נרשם במערכת החיצונית'}>
                          <input
                            type="checkbox"
                            checked={slot.isSynced || false}
                            onChange={() => handleToggleSync(slot.appointmentId!)}
                            disabled={syncingIds.has(slot.appointmentId)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                          />
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <span className="text-slate-400 text-xs">-</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-900">
                      <div className="flex flex-col">
                        <span className="font-medium">{slot.clinicName || '-'}</span>
                        {slot.createdBy && (
                          <span className="text-[10px] text-slate-500 mt-0.5">{slot.createdBy}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs font-medium">
                      <div className="flex gap-1">
                        {slot.isAvailable ? (
                          <div className="relative">
                            <button
                              onClick={() => setOpenDropdownId(openDropdownId === slot.time ? null : slot.time)}
                              className="text-[10px] bg-slate-600 text-white px-2 py-1 rounded hover:bg-slate-700 flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                              פעולות
                            </button>

                            {openDropdownId === slot.time && (
                              <div className="absolute left-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-slate-200">
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleBookSlot(slot);
                                    }}
                                    className="w-full text-right px-4 py-2 text-xs text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    קבע תור
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleCloseSlot(slot);
                                    }}
                                    disabled={closingSlot}
                                    className="w-full text-right px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    סגור שעה
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : slot.status === 'closed' ? (
                          <button
                            onClick={() => handleOpenSlot(slot)}
                            disabled={closingSlot}
                            className="text-[10px] bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                            פתח שעה
                          </button>
                        ) : (
                          <div className="relative">
                            <button
                              onClick={() => setOpenDropdownId(openDropdownId === slot.appointmentId ? null : slot.appointmentId)}
                              className="text-[10px] bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                              פעולות
                            </button>

                            {openDropdownId === slot.appointmentId && (
                              <div className="absolute left-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-slate-200">
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleEditAppointment(slot);
                                    }}
                                    className="w-full text-right px-4 py-2 text-xs text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    ערוך
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleOpenMoveModal(slot);
                                    }}
                                    className="w-full text-right px-4 py-2 text-xs text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                                    title="העבר תור לתאריך או שעה אחרת"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    </svg>
                                    העבר
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      handleOpenCancelModal(slot);
                                    }}
                                    className="w-full text-right px-4 py-2 text-xs text-red-700 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    בטל תור
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {/* Reserve Slot Button Row */}
            <tr className="bg-slate-50 border-t-2 border-slate-300">
              <td colSpan={10} className="px-6 py-4">
                <button
                  onClick={handleCreateReserveSlot}
                  className="w-full py-3 border-2 border-dashed border-teal-400 text-teal-600 rounded-lg font-medium hover:bg-teal-50 hover:border-teal-500 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>הוסף שעה שמורה (Reserve Slot)</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {slots.length === 0 && (
        <div className="text-center p-8 text-slate-500">
          אין שעות זמינות ליום זה
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">
                {isEditMode ? 'עריכת תור' : 'קביעת תור חדש'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">תעודת זהות *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{9}"
                  required
                  autoFocus
                  aria-invalid={Boolean(visibleIdCardError)}
                  aria-describedby="slot-booking-id-card-error"
                  onBlur={() => setIdCardTouched(true)}
                  value={bookingForm.idCard}
                  onChange={(e) => {
                    // Only allow numeric input and limit to 9 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                    setBookingForm({...bookingForm, idCard: value});
                  }}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters from being typed
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  dir="ltr"
                  maxLength={9}
                />
                <p id="slot-booking-id-card-error" role={visibleIdCardError ? 'alert' : undefined} className={`text-xs mt-1 ${visibleIdCardError ? 'text-red-600' : 'text-slate-500'}`}>
                  {visibleIdCardError || 'יש להזין 9 ספרות בלבד'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">שם מלא</label>
                <input
                  type="text"
                  required
                  value={bookingForm.fullName}
                  onChange={(e) => setBookingForm({...bookingForm, fullName: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">מספר טלפון</label>
                <input
                  type="tel"
                  required
                  value={bookingForm.phoneNumber}
                  onChange={(e) => setBookingForm({...bookingForm, phoneNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">יישוב</label>
                <input
                  type="text"
                  required
                  value={bookingForm.village}
                  onChange={(e) => {
                    setBookingForm({...bookingForm, village: e.target.value});
                    setShowVillageDropdown(true);
                  }}
                  onFocus={() => setShowVillageDropdown(true)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="התחל להקליד שם יישוב..."
                />
                {showVillageDropdown && filteredVillages.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredVillages.map((village) => (
                      <div
                        key={village.id}
                        onClick={() => {
                          setBookingForm({...bookingForm, village: village.name});
                          setShowVillageDropdown(false);
                        }}
                        className="px-3 py-2 hover:bg-indigo-50 cursor-pointer transition-colors"
                      >
                        {village.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">קופת חולים</label>
                <select
                  required
                  value={bookingForm.provider}
                  onChange={(e) => setBookingForm({...bookingForm, provider: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">בחר קופת חולים</option>
                  <option value="כללית">כללית</option>
                  <option value="מאוחדת">מאוחדת</option>
                  <option value="פרטי">פרטי</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">שירות</label>
                <select
                  required
                  value={bookingForm.serviceId}
                  onChange={(e) => {
                    const selectedService = filteredServices.find(s => s.service_id.toString() === e.target.value);
                    setBookingForm({
                      ...bookingForm,
                      serviceId: e.target.value
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">בחר שירות</option>
                  {filteredServices.length === 0 && (
                    <option value="" disabled>טוען שירותים...</option>
                  )}
                  {filteredServices.map((service) => (
                    <option key={service.service_id} value={service.service_id}>
                      {service.name}
                    </option>
                  ))}
                </select>
                {selectedSlot?.technicianName && filteredServices.length < services.length && (
                  <p className="text-xs text-blue-600 mt-1">
                    מוצגים רק שירותים עבור {selectedSlot.technicianName}
                  </p>
                )}
                {filteredServices.length === 0 && services.length > 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    אין שירותים זמינים {selectedSlot?.technicianName ? `עבור ${selectedSlot.technicianName}` : `עבור ${activeTab === 'US' ? 'אולטרסאונד' : 'צילום רנטגן'}`}
                  </p>
                )}
              </div>

              {isReserveSlot && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">שעת תור (Reserve Slot)</label>
                  <input
                    type="time"
                    required
                    value={reserveTime}
                    onChange={(e) => {
                      setReserveTime(e.target.value);
                      setSelectedSlot({...selectedSlot, time: e.target.value});
                    }}
                    className="w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-amber-600 mt-1">תור חריג מחוץ לשעות הקבועות</p>
                </div>
              )}

              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">תאריך:</span> {new Date(selectedDate).toLocaleDateString('he-IL')}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">שעה:</span> {isReserveSlot ? (reserveTime || 'נא לבחור שעה') : selectedSlot.time}
                </p>
                {isReserveSlot && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-amber-700 bg-amber-100 rounded">
                    Reserve Slot
                  </span>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting || !isValidIdCard(bookingForm.idCard)}
                  className="flex-1 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed font-medium"
                >
                  {submitting ? 'שומר...' : 'אשר תור'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-300 font-medium"
                >
                  ביטול
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && appointmentToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">
                {appointmentToCancel.status === 'closed' ? 'פתיחת שעה' : 'ביטול תור'}
              </h3>
              <button
                onClick={handleCloseCancelModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-md">
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-medium">שם מטופל:</span> {appointmentToCancel.patientName}
                </p>
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-medium">תאריך:</span> {new Date(selectedDate).toLocaleDateString('he-IL')}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">שעה:</span> {appointmentToCancel.time}
                </p>
              </div>

              {appointmentToCancel.status !== 'closed' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">סיבת ביטול *</label>
                  <select
                    required
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">בחר סיבת ביטול</option>
                    <option value="patient_cancelled">המטופל ביטל את התור</option>
                    <option value="clinic_cancelled">המרפאה ביטלה את התור</option>
                  </select>
                </div>
              )}

              <div className={`${appointmentToCancel.status === 'closed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-md p-3`}>
                <p className={`text-sm ${appointmentToCancel.status === 'closed' ? 'text-green-800' : 'text-red-800'}`}>
                  <strong>שים לב:</strong> {appointmentToCancel.status === 'closed' ? 'פתיחת השעה תאפשר לקבוע תורים חדשים.' : 'ביטול התור יפנה את השעה ויאפשר לקבוע תור חדש.'}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCancelAppointment}
                  disabled={cancelling || (appointmentToCancel.status !== 'closed' && !cancelReason)}
                  className={`flex-1 ${appointmentToCancel.status === 'closed' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white py-2 rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed font-medium`}
                >
                  {cancelling ? (appointmentToCancel.status === 'closed' ? 'פותח...' : 'מבטל...') : (appointmentToCancel.status === 'closed' ? 'אשר פתיחה' : 'אשר ביטול')}
                </button>
                <button
                  type="button"
                  onClick={handleCloseCancelModal}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-300 font-medium"
                >
                  חזור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Appointment Modal */}
      {showMoveModal && appointmentToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">העברת תור</h3>
              <button
                onClick={handleCloseMoveModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-md">
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-medium">שם מטופל:</span> {appointmentToMove.patientName}
                </p>
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-medium">שירות:</span> {appointmentToMove.serviceName}
                </p>
                <p className="text-sm text-slate-600 mb-1">
                  <span className="font-medium">תאריך נוכחי:</span> {new Date(selectedDate).toLocaleDateString('he-IL')}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">שעה נוכחית:</span> {appointmentToMove.time}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">תאריך חדש *</label>
                <input
                  type="date"
                  required
                  value={moveNewDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    setMoveNewDate(e.target.value);
                    handleLoadMoveSlotsForDate(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {loadingMoveSlots && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="mr-3 text-sm text-slate-600">טוען שעות פנויות...</span>
                </div>
              )}

              {!loadingMoveSlots && moveNewDate && moveAvailableSlots.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>שים לב:</strong> לא נמצאו שעות פנויות בתאריך זה עבור סוג השירות ({appointmentToMove.serviceName}).
                  </p>
                </div>
              )}

              {!loadingMoveSlots && moveAvailableSlots.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">שעה חדשה *</label>
                  <select
                    required
                    value={moveSelectedTime}
                    onChange={(e) => setMoveSelectedTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">בחר שעה</option>
                    {moveAvailableSlots.map((slot) => (
                      <option key={slot.appointmentId || `${slot.time}-${slot.technicianId || "empty"}`} value={slot.time}>
                        {slot.time}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {moveAvailableSlots.length} שעות פנויות זמינות
                  </p>
                </div>
              )}

              {moveNewDate && moveSelectedTime && (
                <div className="bg-purple-50 border-purple-200 border rounded-md p-3">
                  <p className="text-sm text-purple-800">
                    <strong>שים לב:</strong> התור יועבר לתאריך {new Date(moveNewDate).toLocaleDateString('he-IL')} בשעה {moveSelectedTime}.
                    סטטוס הסנכרון יאופס.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleMoveAppointment}
                  disabled={movingAppointment || !moveNewDate || !moveSelectedTime}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed font-medium"
                >
                  {movingAppointment ? 'מעביר...' : 'אשר העברה'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseMoveModal}
                  disabled={movingAppointment}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-300 font-medium disabled:opacity-50"
                >
                  חזור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSchedule;
